<template>
  <div v-if="$device.isMobile" class="-my-3 font-semibold">
    <Transition name="fade-slide" mode="out-in">
      <UButton
        :key="mobileMenuIsOpen ? 'close' : 'open'"
        variant="link"
        data-test="open-mobile-menu"
        class="pt-4 -my-4 w-12"
        :padded="false"
        @click="toggleMenu"
      >
        <UIcon
          :class="iconClasses"
          class="text-6xl"
          v-bind="iconAttrs"
          name="i-fluent-text-align-justify-24-regular"
          data-test="icon"
        />
      </UButton>
    </Transition>
    <USlideover v-bind="$attrs" v-model="mobileMenuIsOpen">
      <div class="px-6 py-8 flex flex-col">
        <div class="flex mb-8 justify-between items-center">
          <UButton
            variant="link"
            icon="i-fluent-dismiss-16-filled"
            :padded="false"
            class="self-start focus:!ring-0 focus-visible:!ring-0 !text-gray-600"
            size="xl"
            @click="toggleMenu"
          />

          <ASelectUiLocale />
        </div>

        <slot name="accountLogin">
          <div v-if="$me" class="flex mb-6">
            <UAvatar
              class="mr-3"
              :alt="$me.fullname"
              :ui="{ rounded: 'rounded-xl', placeholder: 'text-gray-900' }"
              size="lg"
            />
            <div class="flex flex-col items-start space-y-0.5 justify-center">
              <p class="text-[1rem] font-bold">
                {{ $t("components.aMobileMenu.hello") }} {{ $me.firstname }}
              </p>
              <ULink
                :to="$localePath({ name: 'account' })"
                class="text-xs underline -mt-1 font-semiboldtext-gray-700"
              >
                {{ $t("components.aMobileMenu.account") }}
              </ULink>
            </div>
          </div>
          <div v-else>
            <UButton
              class="mb-4 rounded-full text-sm px-0"
              size="xl"
              color="white"
              variant="soft"
              @click="authFormModal.open()"
            >
              <UIcon name="fluent:person-circle-32-regular" class="text-4xl" />
              <div class="flex flex-col items-start justify-center">
                <p class="text-base font-bold">
                  {{ $t("components.aMobileMenu.login") }}
                </p>
              </div>
            </UButton>
          </div>
        </slot>

        <div
          v-for="link in menuItems"
          :key="link.label"
          class="py-3 text-[0.95rem] text-black flex items-center space-x-2"
          :class="link.class"
        >
          <UIcon v-if="link.icon" :name="link.icon" class="text-2xl" />
          <ULink :to="link.to ? $localePath(link.to) : ''" @click="link.click">
            {{ $t(link.label) }}
          </ULink>
        </div>

        <slot name="appendMenuItems"></slot>
      </div>
    </USlideover>
  </div>
</template>

<script lang="tsx" setup>
import type { MenuItem } from '~/stores/useMenuStore'
import { twMerge } from 'tailwind-merge'

const props = defineProps<{
  iconAttrs?: Record<string, any>
  links?: MenuItem[]
}>()
const { $me } = useNuxtApp()
const { signOut } = useAuth()
const { authFormModal } = useAuthFormModal()
const localePath = useLocalePath()
const { getMenuItems } = useMenuStore()

const menuItems = getMenuItems('mobile')

menuItems.value.unshift({
  icon: 'mdi:home',
  label: 'components.aMobileMenu.home',
  position: -100,
  to: '/',
})

if ($me.value) {
  menuItems.value.push({
    class: '!text-red-600',
    click: () => signOut({ callbackUrl: localePath({ name: 'index' }) }),
    icon: 'mdi:account',
    label: 'components.aMobileMenu.logout',
    position: 10000,
  })
}

const mobileMenuIsOpen = ref(false)

function toggleMenu() {
  mobileMenuIsOpen.value = !mobileMenuIsOpen.value
}

watch(
  () => useRoute().fullPath,
  () => {
    mobileMenuIsOpen.value = false
  },
)

const iconClasses = computed(() => {
  return twMerge('text-black active:text-primary', props.iconAttrs?.class)
})
</script>

<style>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.1s,
    transform 0.2s;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(-1rem) scale(0.9);
}

.fade-slide-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.fade-slide-leave-from {
  opacity: 0;
  transform: translateX(0);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-2.5rem) scale(0.9);
}
</style>
